import { useState } from 'react'
import { UserAuth } from '../../../context/AuthContext'
import { Link, useLocation, useNavigate } from 'react-router-dom'

// components
import CustomButton from '../ui/CustomButton'
import { sileo } from 'sileo'
import { useGlobalContext } from '../../../context/GlobalState'
import {
  getRegistrationPath,
  REGISTRATION_STATUS
} from '../../../utils/authSession'

const SignInForm = () => {
  const { login } = UserAuth()
  const { ui } = useGlobalContext()
  const navigate = useNavigate()
  const location = useLocation()
  const continuationEmail =
    typeof location.state?.email === 'string' ? location.state.email : ''

  const [formValues, setFormValues] = useState({
    email: continuationEmail,
    password: ''
  })
  const [errors, setErrors] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [simpleAlert, setSimpleAlert] = useState({ visible: false, message: '', severity: 'error' })

  const handleChange = e => {
    const { name, value } = e.target
    setFormValues(prev => ({ ...prev, [name]: value }))
  }

  const handleClickShowPassword = () => setShowPassword(v => !v)

  const validateFields = () => {
    const errs = {}
    if (!formValues.email) errs.email = 'Ingrese su correo electrónico.'
    if (!formValues.password) errs.password = 'Ingrese su contraseña.'
    return errs
  }

  const handleErrors = res => {
    if (res.status === 500) {
      setSimpleAlert({
        visible: true,
        message: 'Algo ha pasado, contacta a soporte técnico',
        severity: 'error'
      })
    } else if (res.status === 401) {
      setErrors({ email: 'Correo o contraseña incorrectos' })
      setSimpleAlert({
        visible: true,
        message: 'Correo o contraseña incorrectos',
        severity: 'error'
      })
    } else if (res.status === 404) {
      setErrors({ email: 'Usuario no encontrado' })
      setSimpleAlert({
        visible: true,
        message: 'No se encontró el usuario en la base de datos',
        severity: 'error'
      })
    } else if (res.status === 403) {
      setSimpleAlert({
        visible: true,
        message: 'Tu cuenta o tu organización fue desactivada. Contacta a soporte.',
        severity: 'error'
      })
    } else if (res.status === 400) {
      setErrors({ email: 'Correo inválido' })
      setSimpleAlert({
        visible: true,
        message: 'Correo inválido',
        severity: 'error'
      })
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const errs = validateFields()
    setErrors(errs)
    setSimpleAlert({ visible: false, message: '', severity: 'error' })

    if (Object.keys(errs).length === 0) {
      try {
        ui.setLoader({ visible: true })
        const res = await login(formValues.email, formValues.password)

        if (
          res.status === 200 ||
          res.registrationStatus === REGISTRATION_STATUS.EMAIL_VERIFICATION_PENDING
        ) {
          if (res.registrationStatus === REGISTRATION_STATUS.PROFILE_PENDING) {
            sileo.info({
              title: 'Registro Pendiente',
              description: 'Completa tus datos para continuar con el registro.'
            })
          } else if (res.registrationStatus === REGISTRATION_STATUS.ORGANIZATION_PENDING) {
            sileo.info({
              title: 'Configuración Pendiente',
              description: 'Necesitas asignar una organización antes de continuar.'
            })
          } else if (
            res.registrationStatus === REGISTRATION_STATUS.EMAIL_VERIFICATION_PENDING
          ) {
            sileo.info({
              title: 'Verificación Pendiente',
              description: 'Verifica tu correo para continuar con el registro.'
            })
          } else {
            sileo.success({
              title: 'Bienvenido',
              description: 'Sesión iniciada con éxito 👌'
            })
          }

          navigate(getRegistrationPath(res.registrationStatus), { replace: true })
        } else {
          handleErrors(res)
        }
      } finally {
        ui.setLoader({ visible: false })
      }
    }
  }

  return (
  <div className='w-full max-w-md mx-auto'>
    <form
      onSubmit={handleSubmit}
       className="
          flex flex-col gap-8 
          bg-transparent lg:bg-white 
          p-10 
          rounded-2xl 
          shadow-lg 
          
          border border-white      /* MOBILE: borde blanco */
          lg:border-gray-100       /* DESKTOP: borde gris */
          
          text-white lg:text-gray-800
        "
          >
        <div className='flex flex-col gap-1'>
          <h1 className='text-2xl font-sans text-center font-semibold text-white lg:text-gray-800'>
            ¡Bienvenido de vuelta!
          </h1>

          <p className='font-sans text-sm text-white/70 lg:text-gray-500 text-center'>
            Inicia sesión para entrar o continuar un registro pendiente
          </p>
        </div>
      {/* 🔹 EMAIL */}
      <div>
        <input
          name='email'
          value={formValues.email}
          onChange={handleChange}
          placeholder='Correo electrónico'
          className='w-full px-5 py-4 rounded-full bg-gray-100 border border-transparent  focus:border-primary focus:bg-white  outline-none font-sans text-sm text-black placeholder:text-black/50 '
          />
        {errors.email && (
          <p className='font-sans text-xs text-red-500 mt-1'>{errors.email}</p>
        )}
      </div>

      {/* 🔹 PASSWORD */}
      <div>
        <div className='relative'>
          <input
            name='password'
            type={showPassword ? 'text' : 'password'}
            value={formValues.password}
            onChange={handleChange}
            placeholder='Contraseña'
            className='w-full px-5 py-4 rounded-full bg-gray-100 border border-transparent  focus:border-primary focus:bg-white  outline-none font-sans text-sm text-black placeholder:text-black/50 '
          />

          <button
            type='button'
            onClick={handleClickShowPassword}
            className='absolute right-4 top-1/2 -translate-y-1/2 text-black/70 lg:text-gray-500 font-sans text-sm'
          >
            {showPassword ? 'Ocultar' : 'Ver'}
          </button>
        </div>

        {errors.password && (
          <p className='font-sans text-xs text-red-500 mt-1'>{errors.password}</p>
        )}
      </div>

      {/* 🔹 ALERTA */}
      {simpleAlert.visible && (
        <div
          className={`font-sans text-sm px-4 py-3 rounded-xl ${
            simpleAlert.severity === 'error'
              ? 'bg-red-500/20 text-white lg:bg-red-50 lg:text-red-600'
              : 'bg-green-500/20 text-white lg:bg-green-50 lg:text-green-600'
          }`}
        >
          {simpleAlert.message}
        </div>
      )}

      {/* 🔹 BOTÓN */}
      <CustomButton
        type="submit"
        className="w-full py-4 rounded-xl bg-accent2 lg:bg-primary lg:to-accent2 text-white font-semibold font-sans text-sm shadow-md border border-white"
        title="Ir a mi cuenta"
      />

      {/* 🔹 LINKS */}
      <div className='flex flex-col items-center gap-1 text-center'>
        <Link
          to='/auth/reset-password'
          className='font-sans text-sm text-white lg:text-accent1 hover:underline'
        >
          ¿No recuerdas tu contraseña?
        </Link>

        <p className='font-sans text-sm text-white lg:text-gray-500'>
          ¿No tienes cuenta?{' '}
          <Link
            to='/auth/signup'
            className='text-white lg:text-accent1 hover:underline'
          >
            Crea tu cuenta aquí
          </Link>
        </p>
      </div>
    </form>

    {/* 🔹 TÉRMINOS */}
    <p className='font-sans text-xs text-white/70 lg:text-gray-500 text-center mt-4 leading-relaxed'>
      Siempre puedes revisar <br />
      nuestros{' '}
      <a
        href='/TRYLUCA%20T%26C.pdf'
        target='_blank'
        rel='noopener noreferrer'
        className='font-sans text-white lg:text-accent1 hover:underline'
      >
        Términos y Condiciones
      </a>
    </p>
  </div>
)
}

export default SignInForm
